import { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from '../../../global/Button/Button';

// mutations
import { SharedSaveSearch } from '../../../mutations/SharedSaveSearch';

// styles
import styles from './FollowButton.scss';

// helpers
import get from 'lodash.get';
import classnames from 'classnames';
import { FOLLOW_DEALER_FLOW, sharedLoginCheck } from '../../../authentication/sharedLoginCheck';
import { locations, authLabels } from '../../../utils/tracking/shared/followSearchTracking';
import { getFollowTypeFromUri } from '../../../utils/favoritesUtils';
import { TYPES } from '../../../constants/favoritesConstants';

export class FollowButtonComponent extends Component {
    constructor(props) {
        super(props);

        this.state = {
            userId: null,
            loading: false,
            waitForConnection: false,
            autoFollow: props.autoFollow,
        };

        this.handleClick = this.handleClick.bind(this);
        this.handleFollowCompleted = this.handleFollowCompleted.bind(this);
        this.handleLoginSuccess = this.handleLoginSuccess.bind(this);
        this.handleLoginError = this.handleLoginError.bind(this);
        this.handleFollow = this.handleFollow.bind(this);
    }

    componentDidUpdate() {
        const { waitForConnection, userId, autoFollow } = this.state;

        /**
         * We must wait for the user fetch before sending off the mutation. We do this by checking
         * whether the favorites connection exists.
         *
         * There's a possibility that the mutation will resolve before the user fetch returns,
         * so the newly returned mutation result will be overridden with whatever comes from the
         * user query.
         */
        if (waitForConnection && userId && this.props.favoritesConnection) {
            this.setState({ waitForConnection: false }, () => {
                this.handleFollow({ userId });
            });
        }

        /**
         * If autoFollow is true, when the component renders it should try and follow the search automatically.
         * We need to consider both situations when the user is and is not logged in.
         *
         * Case 1:
         *
         * User is logged in, so userId is present in props.  If this is the case we need to make sure
         * favoritesConnection is populated (this information is asynced in).  Once we have this information
         * we check to make sure the user is already not following the search.
         * If they are not we trigger the click event on the button to handle the following logic.
         */
        if (autoFollow && this.props.userId && this.props.favoritesConnection) {
            this.setState({ autoFollow: false }, () => {
                if (!this.getFavoriteId()) {
                    this.handleClick();
                }
            });
        }

        /**
         * Case 2:
         *
         * In this case the user is not logged in so we trigger the handleClick function to follow the search.
         * At this point we need to know the user is not logged in, even though we make this check in the handleClick function,
         * because if the user is logged in we need to make sure favoritesConnection is populated (see Case 1)
         *
         */
        if (autoFollow && !this.props.userId) {
            this.setState({ autoFollow: false }, () => {
                this.handleClick();
            });
        }
    }

    handleLoginError() {
        this.setState({ loading: false });
    }

    getFavoriteId() {
        return get(this.props, 'favoritesConnection.edges[0].node.serviceId');
    }
    handleFollow({ userId, favoritedId }) {
        SharedSaveSearch.commit(this.props.relay.environment, {
            userId,
            favoritedSearchId: favoritedId,
            page: this.props.page,
            name: this.props.name,
            type: getFollowTypeFromUri(this.props.page),
            onCompleted: this.handleFollowCompleted,
            onError: this.handleFollowCompleted,
        });
    }
    showAuthModal() {
        sharedLoginCheck({
            onSuccess: this.handleLoginSuccess,
            onError: this.handleLoginError,
            authOptions: {
                flow: FOLLOW_DEALER_FLOW,
                ga: { label: authLabels[this.props.location] },
            },
        });
    }
    handleLoginSuccess({ userId }) {
        this.setState({
            waitForConnection: true,
            userId,
        });
    }
    handleClick() {
        this.setState({ loading: true }, () => {
            const { userId } = this.props;
            if (userId) {
                this.handleFollow({
                    userId,
                    favoritedId: this.getFavoriteId(),
                });
            } else {
                this.showAuthModal();
            }
        });
    }
    handleFollowCompleted(response) {
        const isFollowing = get(response, 'favoritePage.user.savedSearches.edges.0.node.serviceId');

        this.setState({ loading: false }, () => {
            this.props.onFollowSearchCompleted(isFollowing);
        });
    }

    getFollowMessage() {
        if (this.getFavoriteId()) {
            return (
                <FormattedMessage id="shared.FollowButton.following" defaultMessage="Following" />
            );
        }

        switch (getFollowTypeFromUri(this.props.page)) {
            case TYPES.SEARCH:
                return <FormattedMessage id="shared.FollowButton.follow" defaultMessage="Follow" />;
            case TYPES.DEALER:
                return (
                    <FormattedMessage
                        id="shared.FollowButton.followSeller"
                        defaultMessage="Follow Seller"
                    />
                );
            default:
                return null;
        }
    }

    render() {
        const { size, fullWidth, type } = this.props;
        const className = classnames(styles.button, this.props.className);

        return (
            <Button
                className={className}
                disabled={this.state.loading}
                size={size}
                isSecondary={size !== 'large' && type !== 'primary'}
                fullWidth={fullWidth}
                onClick={this.handleClick}
                dataTn="item-actions-follow-search"
            >
                {this.getFollowMessage()}
            </Button>
        );
    }
}

FollowButtonComponent.propTypes = {
    className: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    fullWidth: PropTypes.bool,
    userId: PropTypes.string,
    user: PropTypes.object,
    relay: PropTypes.object,
    page: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onFollowSearchCompleted: PropTypes.func,
    favoritesConnection: PropTypes.object,
    autoFollow: PropTypes.bool,
    location: PropTypes.oneOf(Object.values(locations)),
    type: PropTypes.oneOf(['primary', 'secondary']),
};

FollowButtonComponent.defaultProps = {
    size: 'large',
    fullWidth: false,
    autoFollow: false,
    favoritesConnection: PropTypes.object,
    onFollowSearchCompleted: () => ({}),
};

export const FollowButton = createFragmentContainer(FollowButtonComponent, {
    favoritesConnection: graphql`
        fragment FollowButton_favoritesConnection on favoritesConnection {
            edges {
                node {
                    serviceId
                }
            }
        }
    `,
});
